import React from 'react';
import { Sparkles, Image as ImageIcon, Plus, Trash2, CheckCircle2 } from 'lucide-react';
import { PageSection } from '../../../../types';
import { ActionLinkInput } from '../common/ActionLinkInput';

interface AboutEditorProps {
  section: PageSection;
  onUpdateContent: (updates: any) => void;
}

export const AboutEditor: React.FC<AboutEditorProps> = ({ section, onUpdateContent }) => {
  const content = section.content || {};
  const highlights: string[] = content.highlights || [
    '25+ yıllık kurumsal ve operasyonel tecrübe',
    'Uluslararası kalite ve regülasyon standartları',
    'Kişiye ve kuruma özel esnek çözümler',
  ];

  const handleAddHighlight = () => {
    const next = [...highlights, `Yeni Öne Çıkan Madde ${highlights.length + 1}`];
    onUpdateContent({ highlights: next });
  };

  const handleUpdateHighlight = (index: number, text: string) => {
    const next = [...highlights];
    next[index] = text;
    onUpdateContent({ highlights: next });
  };

  const handleDeleteHighlight = (index: number) => {
    const next = highlights.filter((_, i) => i !== index);
    onUpdateContent({ highlights: next });
  };

  return (
    <div className="space-y-6">
      {/* Headings */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3.5">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-blue-400" />
          <h4 className="text-xs font-bold text-slate-200">Hakkımızda Metinleri</h4>
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Üst Rozet (Badge)</label>
          <input
            type="text"
            value={content.badge ?? 'KURUMSAL KİMLİK'}
            onChange={(e) => onUpdateContent({ badge: e.target.value })}
            placeholder="Örn: KURUMSAL KİMLİK"
            className="w-full bg-slate-900 border border-slate-700 text-xs font-semibold text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Ana Başlık</label>
          <input
            type="text"
            value={content.title ?? 'Değer Üreten, Güven İnşa Eden Global Birikim'}
            onChange={(e) => onUpdateContent({ title: e.target.value })}
            placeholder="Başlık..."
            className="w-full bg-slate-900 border border-slate-700 text-xs font-bold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Hikaye / Açıklama</label>
          <textarea
            value={content.description ?? ''}
            onChange={(e) => onUpdateContent({ description: e.target.value })}
            rows={4}
            placeholder="Kurumun felsefesi ve misyonu..."
            className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 p-2.5 rounded-xl outline-none focus:border-blue-500 resize-none"
          />
        </div>
      </div>

      {/* Highlights List */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-3.5 h-3.5 text-blue-400" />
            <h4 className="text-xs font-bold text-slate-200">Öne Çıkan Maddeler ({highlights.length})</h4>
          </div>
          <button
            type="button"
            onClick={handleAddHighlight}
            className="text-[10px] font-bold text-blue-400 hover:text-blue-300 flex items-center gap-1"
          >
            <Plus className="w-3 h-3" />
            <span>Madde Ekle</span>
          </button>
        </div>

        <div className="space-y-2">
          {highlights.map((h, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <input
                type="text"
                value={h || ''}
                onChange={(e) => handleUpdateHighlight(idx, e.target.value)}
                placeholder="Madde metni..."
                className="flex-1 bg-slate-900 border border-slate-700 text-xs text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
              />
              <button
                type="button"
                onClick={() => handleDeleteHighlight(idx)}
                className="p-2 text-slate-500 hover:text-rose-400 transition"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Image URL & Layout */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3">
        <div className="flex items-center gap-2">
          <ImageIcon className="w-3.5 h-3.5 text-blue-400" />
          <h4 className="text-xs font-bold text-slate-200">Görsel & Düzen</h4>
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Görsel URL</label>
          <input
            type="url"
            value={content.imageUrl ?? ''}
            onChange={(e) => onUpdateContent({ imageUrl: e.target.value })}
            placeholder="https://images.unsplash.com/..."
            className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
          />
        </div>

        <div className="pt-1 flex items-center justify-between">
          <span className="text-xs font-bold text-slate-300">Yerleşimi Ters Çevir</span>
          <button
            type="button"
            onClick={() => onUpdateContent({ reverseLayout: !content.reverseLayout })}
            className={`px-3 py-1 rounded-xl text-xs font-bold transition border ${
              content.reverseLayout
                ? 'bg-blue-600 text-white border-blue-500'
                : 'bg-slate-900 text-slate-400 border-slate-700'
            }`}
          >
            {content.reverseLayout ? 'Metin Solda' : 'Görsel Solda'}
          </button>
        </div>
      </div>

      {/* CTA Button */}
      <ActionLinkInput
        label="Daha Fazla Bilgi Butonu"
        buttonText={content.buttonText ?? 'Daha Fazla Bilgi'}
        onButtonTextChange={(txt) => onUpdateContent({ buttonText: txt })}
        linkType={content.buttonLinkType || 'page'}
        onLinkTypeChange={(type) => onUpdateContent({ buttonLinkType: type })}
        linkValue={content.buttonLink || '/hakkimizda'}
        onLinkValueChange={(val) => onUpdateContent({ buttonLink: val })}
      />
    </div>
  );
};
